import React, { useState } from "react"
import { Badge, Button, Card, Form, InputGroup, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import moment from "moment"
import Settings from "@/common/Settings"
import API from "@/common/API"
import { Routes } from "@/routes"
import placeEnhancementsService from "@/services/PlaceEnhancementsService"

interface PlaceRecord {
  id: string
  name: string
  address?: string
  sector?: string
  city?: string
  image?: string
  image2?: string
  created?: string | Date
  updated?: string | Date
  createdDate?: string | Date
  updatedDate?: string | Date
  assignedToObj?: {
    firstName: string
    lastName: string
  }
  stage?: { name: string }
  placeStatus?: { name: string; color?: string; icon?: string }
  category?: { name: string }
}

interface PlaceListViewProps {
  places: PlaceRecord[]
  isLoading?: boolean
  onSelectPlace?: (place: PlaceRecord) => void
}

const PlaceListView: React.FC<PlaceListViewProps> = ({ places, isLoading = false, onSelectPlace }) => {
  const { t } = useTranslation("global")
  const history = useHistory()
  const [searchTerm, setSearchTerm] = useState("")

  const getImageUrl = (img?: string, img2?: string) => {
    const raw = img || img2
    if (!raw) return null
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw
    return `${Settings.BasePath}files/${raw}`
  }

  const filteredPlaces = places.filter(p => {
    if (!searchTerm.trim()) return true
    const term = searchTerm.toLowerCase()
    return (
      p.name.toLowerCase().includes(term) ||
      (p.address && p.address.toLowerCase().includes(term)) ||
      (p.sector && p.sector.toLowerCase().includes(term)) ||
      (p.city && p.city.toLowerCase().includes(term))
    )
  })

  return (
    <Card className="border-0 shadow-sm rounded-3">
      <Card.Header className="bg-white py-3 border-bottom">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="d-flex align-items-center gap-2">
            <i className="fas fa-list-ul text-primary me-1" />
            <span className="fw-bold text-dark fs-6">Lista de Lugares</span>
            <Badge bg="primary" pill className="ms-1">{filteredPlaces.length} registros</Badge>
          </div>
          <div style={{ marginRight: 120, minWidth: 240, maxWidth: 320 }}>
            <InputGroup size="sm" className="rounded-pill border shadow-2xs overflow-hidden">
              <InputGroup.Text className="bg-white border-0 pe-1 text-muted">
                <i className="fas fa-search" />
              </InputGroup.Text>
              <Form.Control
                className="border-0 ps-1 shadow-none"
                placeholder="Buscar por nombre, sector..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>
        </div>
      </Card.Header>

      <Card.Body className="p-0">
        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary spinner-border-sm me-2" role="status" />
            <span className="small text-muted">Cargando lista de lugares...</span>
          </div>
        ) : filteredPlaces.length === 0 ? (
          <div className="text-center py-5 text-muted small">
            <i className="fas fa-location-dot fa-2x mb-2 text-muted" />
            <div>No se encontraron lugares que coincidan con los criterios.</div>
          </div>
        ) : (
          <Table hover responsive className="align-middle small mb-0">
            <thead className="table-light">
              <tr>
                <th className="ps-3 text-center" style={{ width: 60 }}>FOTO</th>
                <th>LUGAR / UBICACIÓN</th>
                <th>SECTOR / CIUDAD</th>
                <th>ETAPA Y ESTADO</th>
                <th>FECHA Y ACTUALIZACIÓN</th>
                <th>AGENTE ASIGNADO</th>
                <th>CONTACTO PRINCIPAL</th>
                <th>CUENTA VINCULADA</th>
                <th className="text-center">RECORDATORIOS</th>
                <th className="text-end pe-3">ACCIONES</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlaces.map(place => {
                const primaryContact = placeEnhancementsService.getPrimaryContact(place.id)
                const pendingReminders = placeEnhancementsService.getAllPendingRemindersCount(place.id)
                const accountLink = placeEnhancementsService.getAccountLink(place.id)
                const imgUrl = getImageUrl(place.image, place.image2)
                const createdDate = place.created || place.createdDate
                const updatedDate = place.updated || place.updatedDate

                return (
                  <tr key={place.id}>
                    {/* Foto */}
                    <td className="ps-3 text-center align-middle" style={{ width: 60 }}>
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={place.name}
                          className="rounded-3 border object-fit-cover shadow-2xs"
                          style={{ width: 44, height: 44, objectFit: "cover" }}
                          onError={e => {
                            (e.target as HTMLElement).style.display = "none"
                          }}
                        />
                      ) : (
                        <div
                          className="rounded-3 bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold border mx-auto"
                          style={{ width: 44, height: 44, fontSize: 16 }}
                        >
                          {place.name ? place.name.charAt(0).toUpperCase() : "L"}
                        </div>
                      )}
                    </td>

                    {/* Lugar / Ubicación */}
                    <td>
                      <div
                        className="fw-bold text-primary clickable"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          if (onSelectPlace) onSelectPlace(place)
                          else history.push(`${Routes.PlaceDetails.clean}${place.id}`)
                        }}
                      >
                        {place.name}
                      </div>
                      {place.address && <small className="text-muted d-block text-truncate" style={{ maxWidth: 220 }}>{place.address}</small>}
                    </td>

                    {/* Sector / Ciudad */}
                    <td>
                      <div className="fw-semibold text-dark">{place.sector || "—"}</div>
                      {place.city && <small className="text-muted d-block">{place.city}</small>}
                    </td>

                    {/* Etapa y Estado */}
                    <td>
                      <div className="d-flex flex-column gap-1">
                        {place.stage && (
                          <Badge bg="light" text="dark" className="border text-start w-auto" style={{ maxWidth: "fit-content" }}>
                            {place.stage.name}
                          </Badge>
                        )}
                        {place.placeStatus && (
                          <span className="small fw-semibold" style={{ color: place.placeStatus.color || "#6c757d" }}>
                            <i className={`${place.placeStatus.icon || "fa fa-circle"} me-1`} />
                            {place.placeStatus.name}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Fecha y Actualización */}
                    <td>
                      <div>
                        {createdDate ? (
                          <span className="text-dark d-block fw-semibold" style={{ fontSize: "0.78rem" }}>
                            <i className="far fa-calendar-plus me-1 text-primary" />
                            {moment(createdDate).format("DD/MM/YYYY")}
                          </span>
                        ) : (
                          <span className="text-muted d-block" style={{ fontSize: "0.75rem" }}>Sin registro</span>
                        )}
                        {updatedDate ? (
                          <small className="text-muted d-block" style={{ fontSize: "0.72rem" }}>
                            <i className="far fa-clock me-1 text-info" />
                            {moment(updatedDate).fromNow()}
                          </small>
                        ) : (
                          <small className="text-muted d-block" style={{ fontSize: "0.72rem" }}>Reciente</small>
                        )}
                      </div>
                    </td>

                    {/* Agente Asignado */}
                    <td>
                      {place.assignedToObj ? (
                        <div className="d-flex align-items-center gap-1 text-dark fw-semibold">
                          <i className="fas fa-user-check text-success me-1" />
                          {place.assignedToObj.firstName} {place.assignedToObj.lastName}
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* Contacto Principal */}
                    <td>
                      {primaryContact ? (
                        <div>
                          <div className="fw-bold text-dark">{primaryContact.name}</div>
                          <small className="text-muted d-block">{primaryContact.position || "Contacto Principal"}</small>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* Cuenta Vinculada */}
                    <td>
                      {accountLink ? (
                        <Badge bg="info" text="white" className="rounded-pill">
                          {accountLink.accountNumber || (accountLink as any).accountName || "Vinculada"}
                        </Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* Recordatorios */}
                    <td className="text-center">
                      {pendingReminders > 0 ? (
                        <Badge bg="warning" text="dark" pill>
                          {pendingReminders} pendientes
                        </Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* Acciones */}
                    <td className="text-end pe-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="py-1 px-2 rounded-3"
                        title="Ver detalles"
                        onClick={() => {
                          if (onSelectPlace) onSelectPlace(place)
                          else history.push(`${Routes.PlaceDetails.clean}${place.id}`)
                        }}
                      >
                        <i className="fas fa-eye" />
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  )
}

export default PlaceListView
